import { Avatar } from "primereact/avatar";
import PropTypes from "prop-types";
import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";

import Menu from "~/components/menu/Menu";
import Attachments from "~/components/Processes/conversations/Attachments";

import {
  downloadFileHandler,
  getAdHocEscalationDisplayName,
  getBackendFileName,
  openPreviewInNewTab,
  resolveDisplayFileName,
} from "../../../utils/fileUtils";
import Categories from "~/components/Processes/conversations/utils/categories.json";

const CommentMessage = ({
  messageInfo,
  deletedAt,
  formatDate,
  setDeleteModal,
  setSelectedMessage,
  conversation,
  relatedType = "",
  processdata = {},
  allCommentAttachments = [],
}) => {
  const [attachmentOverrides, setAttachmentOverrides] = useState({});
  const { id: userId, role: userRole } = useSelector((state) => state.login);
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const getUserDisplayName = (userData) => {
    if (!userData) return "Unknown User";
    const firstName = userData.first_name || "";
    const lastName = userData.last_name || "";
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || "Unknown User";
  };

  const getRoleDisplay = (userData) => {
    if (!userData?.role) return "";
    return role[userData.role] || userData.role;
  };

  const getMessageHeader = () => {
    return (
      <>
        Comment added to the{" "}
        <Link
          to={`/process/${id}/milestones?milestoneId=${messageInfo?.taskData?.milestone}`}
          className="conversation_link"
        >
          {messageInfo?.taskData?.name}
        </Link>{" "}
        under the {messageInfo?.taskData?.milestoneData?.name} by{" "}
        {getUserDisplayName(messageInfo?.userData)}
        {getRoleDisplay(messageInfo?.userData) &&
          ` (${getRoleDisplay(messageInfo?.userData)})`}
      </>
    );
  };

  const menuItems = useMemo(
    () => [
      {
        label: "Delete Message",
        icon: "pi pi-trash",
        command: () => {
          // Remove commentId from URL when delete modal opens
          const params = new URLSearchParams(location.search);
          if (params.has("commentId")) {
            params.delete("commentId");
            params.delete("commentType");
            const newSearch = params.toString();
            const newPath = newSearch
              ? `${location.pathname}?${newSearch}`
              : location.pathname;
            navigate(newPath, { replace: true });
          }

          setDeleteModal(true);
          setSelectedMessage(conversation);
        },
      },
    ],
    [conversation, setDeleteModal, setSelectedMessage, navigate, location],
  );
  const role = {
    GXM_ADMIN: "GXM Admin",
    CUSTOMER: "Customer",
    LSP_USER: "Local Team",
    GXM_USER: "GXM User",
    GXM_MANAGER: "GXM Manager",
    LSP_MANAGER: "Local Team Manager",
    CUSTOMER_MANAGER: "Customer Manager",
  };

  const getSubCategoryName = (categoryCode, subCategoryCode) => {
    const cat = Categories?.find((c) => c.code === categoryCode);
    const sub = cat?.subCategory?.find((s) => s.code === subCategoryCode);
    return sub?.name ?? subCategoryCode ?? "-";
  };

  const getAttachmentKey = (file, index) =>
    file?.blobName || file?.blob_url || file?.id || `${file?.name}-${index}`;

  const mergedAttachmentData = useMemo(
    () =>
      (messageInfo?.attachmentData || []).map((file, index) => {
        const key = getAttachmentKey(file, index);
        return attachmentOverrides[key]
          ? { ...file, ...attachmentOverrides[key] }
          : file;
      }),
    [messageInfo?.attachmentData, attachmentOverrides],
  );

  const isPayrollService =
    (relatedType === "PROCESS" || relatedType === "REQUEST") &&
    processdata?.serviceData?.name === "Payroll";

  const getFormattedAttachmentName = (file) => {
    if (isPayrollService) {
      return getBackendFileName(file) || file?.name;
    }

    const allFilesInScope =
      allCommentAttachments && allCommentAttachments.length
        ? allCommentAttachments
        : mergedAttachmentData;

    const resolved = resolveDisplayFileName(
      file,
      allFilesInScope,
      getSubCategoryName,
    );
    if (resolved) return resolved;

    const fileName = file?.original_file_name || file?.name;
    const subCategory = file?.subCategory;

    if (subCategory && fileName) {
      const humanReadableSubCategory = subCategory
        .toLowerCase()
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");

      return getAdHocEscalationDisplayName(
        humanReadableSubCategory,
        messageInfo?.createdAt,
        fileName,
      );
    }

    return fileName;
  };

  const getContainer = (blobUrl) =>
    blobUrl?.includes("/ess") ? "ess" : "documents";

  const openPreview = (file) =>
    openPreviewInNewTab(
      { objectURL: file?.blob_url },
      getContainer(file?.blob_url),
    );

  const messageText =
    messageInfo?.content ||
    messageInfo?.comment ||
    messageInfo?.message ||
    messageInfo?.messageData?.comment ||
    "";

  return (
    <div className="message-container">
      <Avatar icon={"pi pi-comment"} shape="square" className="task_comment" />
      <div className="message-content">
        <div className="meta-info">
          <p className="user-name">{getMessageHeader()} </p>
          <p className="date-time">{formatDate(messageInfo?.createdAt)}</p>
          {!deletedAt &&
            (userId === messageInfo?.userData?.id ||
              userRole?.startsWith("GXM")) && (
              <div className="menu-container">
                <Menu menuItems={menuItems} />
              </div>
            )}
        </div>
        {messageText && !deletedAt && <p className="message">{messageText}</p>}
        {deletedAt && (
          <p className="message deleted-msg">
            Message removed by {getUserDisplayName(messageInfo?.updatedBy)}
            {getRoleDisplay(messageInfo?.updatedBy) &&
              ` (${getRoleDisplay(messageInfo?.updatedBy)})`}
            .
          </p>
        )}
        {mergedAttachmentData.length > 0 &&
          !deletedAt &&
          mergedAttachmentData.map((file, index) => {
            const attachmentKey = getAttachmentKey(file, index);
            const displayName = getFormattedAttachmentName(file);
            return (
              <Attachments
                key={attachmentKey}
                file={{
                  ...file,
                  createdByData: messageInfo?.userData,
                  createdAt: messageInfo?.updatedAt,
                }}
                displayName={displayName}
                onAttachmentUpdate={(updatedFile) => {
                  setAttachmentOverrides((prev) => ({
                    ...prev,
                    [attachmentKey]: {
                      ...(prev[attachmentKey] || {}),
                      ...updatedFile,
                    },
                  }));
                }}
                icon="pi-download"
                progressBar={false}
                fileHandler={() => downloadFileHandler(file)}
                openPreviewHandler={() => openPreview(file)}
                showPreviewIcon={false}
                showDescriptionIcon={true}
                id={id}
                relatedType={relatedType}
              />
            );
          })}
      </div>
    </div>
  );
};

CommentMessage.proptypes = {
  messageInfo: PropTypes.any,
  formatDate: PropTypes.func,
  status: PropTypes.string,
  allCommentAttachments: PropTypes.array,
};

export default CommentMessage;
